import { CanopyProvider } from '@bloomfieldrobotics/canopy';
import { Box, Button, hexToRgb, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import {
  DataGrid,
  getGridNumericOperators,
  GridAlignment,
  GridColDef,
  GridColumnGroupingModel,
  GridRenderCellParams,
  GridToolbar,
  useGridApiRef,
} from '@mui/x-data-grid';
import dayjs from 'dayjs';
import { motion } from 'framer-motion';
import { useMemo, useRef } from 'react';
import { useResizeDetector } from 'react-resize-detector';
import { useAppSelector } from '../../../../app/hooks';
import {
  BlueberryColorsMap,
  BlueberryFeatures,
  BlueberryFlowerStageFeatures,
  FeatureEnum,
} from '../../../../services/data/impl/cube/cubeSlice';
import { Block } from '../../../../services/entities/impl/blfApi/schemas/block';
import { CropTypes } from '../../../../services/entities/impl/blfApi/schemas/variety';
import { useTranslation } from '../../../../services/translation/hooks/useTranslation';
import {
  CustomerSummaryFilterEnum,
  useFilterCustomerSummary,
} from '../../../customerSummary/hooks/useFilterCustomerSummary';
import { selectIsLoadingCustomerSummary } from '../../../ranches/ranchesSlice';
import { roundFractional } from '../../../ranches/ranchUtils';
import { formatCellContent } from '../../../ranches/utils/common';
import { orderBlueberriesByRipenessOrder } from '../../../scanDataApi/scanDataUtils';
import RanchSummaryTableDropdowns from '../RanchSummaryTableDropdowns';
import {
  BlockMeasureResult,
  BlueberryTableCount,
  useCalculateBlueberryMeasures,
} from './hooks/useCalculateBlueberryMeasures';
const blueberriesInRipenessOrder = orderBlueberriesByRipenessOrder({
  arrayToOrder: BlueberryFeatures,
});

type Props = {
  type?: 'CropEstimates' | 'RanchView';
  blocksFiltered: Block[] | undefined;
  colorScheme?: 'dark' | 'light';
};

/**
 * RanchSummaryTableBlueberry is a React functional component that renders a table view of blueberry data for a ranch.
 *
 * @param {Props} props - The component props.
 * @return {ReactElement} - The rendered table view.
 */
const RanchSummaryTableBlueberry: React.FC<Props> = ({ blocksFiltered }) => {
  const theme = useTheme();
  const apiRef = useGridApiRef();
  const { t } = useTranslation();

  const isLoadingCustomerSummary = useAppSelector(
    selectIsLoadingCustomerSummary
  );

  const flowerFeatures = [...BlueberryFlowerStageFeatures, FeatureEnum.flower];

  const customerSummaryFilters = [
    CustomerSummaryFilterEnum.ranch,
    CustomerSummaryFilterEnum.block,
    CustomerSummaryFilterEnum.feature,
    CustomerSummaryFilterEnum.highestAIServiceVersion,
  ];

  const filteredCustomerSummary = useFilterCustomerSummary({
    activeFilters: customerSummaryFilters,
    cropTypes: [CropTypes.Blueberries],
  });

  const rows = useCalculateBlueberryMeasures({
    blockSummary: filteredCustomerSummary,
    blocksFiltered,
  });

  // Get width of parent element around data grid
  const sizeRef = useRef<HTMLDivElement>(null);
  const size = useResizeDetector({
    targetRef: sizeRef,
    refreshMode: 'debounce',
    refreshRate: 200,
  });

  const cells: GridColDef[] = useMemo(() => {
    const minColumnWidthPrimary = 100;
    const minColumnWidthSmall = 60;

    const commonColDefProps = {
      flex: 1,
      minWidth: minColumnWidthPrimary,
      align: 'left' as GridAlignment,
      editable: false,
      headerAlign: 'left' as GridAlignment,
    };

    const columns: GridColDef[] = [
      {
        headerName: t('Scan Date'),
        field: 'scanDate',
        type: 'string',
        valueFormatter: (value) => {
          if (value) {
            return dayjs(value).format('MM/DD/YYYY');
          }
        },
      },
      {
        headerName: t('ranch'),
        field: 'ranchName',
        type: 'string',
      },
      {
        headerName: t('block'),
        field: 'blockName',
        type: 'string',
        sortComparator: (v1: string, v2: string) =>
          v1.localeCompare(v2, undefined, {
            numeric: true,
            sensitivity: 'base',
          }),
      },
      ...BlueberryFlowerStageFeatures.map(
        (feature) =>
          ({
            // Column for count value
            headerName: t(feature),
            field: feature + '_value',
            headerClassName:
              feature === flowerFeatures.at(0) ? 'left-divider' : null,
            type: 'string',
            renderHeader: () => (
              <Typography
                sx={{
                  color: BlueberryColorsMap[feature],
                  font: 'inherit',
                }}
              >
                {/* Make flowers plural */}
                {feature === FeatureEnum.flower ? t('Flowers') : t(feature)}
              </Typography>
            ),
            valueGetter: (_value, row) =>
              row[feature as keyof BlockMeasureResult].value,
            renderCell: ({ row }: GridRenderCellParams<BlockMeasureResult>) => {
              const entry = row[
                feature as keyof BlockMeasureResult
              ] as BlueberryTableCount;
              return (
                <Box>
                  {entry?.value != null ? roundFractional(entry.value, 0) : '-'}
                </Box>
              );
            },
          } as GridColDef)
      ),
      {
        headerName: t('Total', { ns: 'ranch-view' }),
        field: 'totalEstimatedFlowerCount',
        type: 'number',
        filterOperators: getGridNumericOperators(),
        valueFormatter: (value) =>
          formatCellContent(value, { maxFractionDigits: 0 }),
      },
      {
        headerName: t('Distribution'),
        field: 'flowerStageDistribution',
        renderCell: (params) => {
          return BlueberryFlowerStageFeatures.some(
            (feature) => params.row[feature].value
          )
            ? renderMiniBarChartCell(params, BlueberryFlowerStageFeatures)
            : '-';
        },
      },
      ...BlueberryFlowerStageFeatures.map(
        (feature) =>
          ({
            // Column for count value
            headerName: t(feature),
            field: feature + '_value_per_plant',
            headerClassName:
              feature === flowerFeatures.at(0) ? 'left-divider' : null,
            type: 'string',
            renderHeader: () => (
              <Typography
                sx={{
                  color: BlueberryColorsMap[feature],
                  font: 'inherit',
                }}
              >
                {/* Make flowers plural */}
                {feature === FeatureEnum.flower ? t('Flowers') : t(feature)}
              </Typography>
            ),
            valueFormatter: (value) =>
              formatCellContent(value, { maxFractionDigits: 0 }),
            valueGetter: (_value, row) =>
              (row[feature as keyof BlockMeasureResult] as BlueberryTableCount)
                .perPlant,
          } as GridColDef)
      ),
      {
        field: 'totalFlowersPerPlant',
        headerName: t('Total', { ns: 'ranch-view' }),
        type: 'number',
        filterOperators: getGridNumericOperators(),
        valueFormatter: (value) =>
          formatCellContent(value, { maxFractionDigits: 0 }),
      },
      ...blueberriesInRipenessOrder.flatMap((feature) => [
        {
          // Column for count value
          headerName: t(feature),
          minWidth: 75,
          field: feature + '_value',
          headerClassName:
            feature === blueberriesInRipenessOrder.at(0)
              ? 'left-divider'
              : null,
          type: 'string',
          renderHeader: () => (
            <Typography
              sx={{
                color: BlueberryColorsMap[feature],
                font: 'inherit',
              }}
            >
              {t(feature)}
            </Typography>
          ),
          valueGetter: (value, row) =>
            (row?.[feature as keyof BlockMeasureResult] as BlueberryTableCount)
              ?.value,
          renderCell: (params: GridRenderCellParams<BlockMeasureResult>) => {
            const entry = params.row[
              feature as keyof BlockMeasureResult
            ] as BlueberryTableCount;
            return (
              <Box>
                {entry?.value != null ? roundFractional(entry?.value, 0) : '-'}
              </Box>
            );
          },
        } as GridColDef,
        {
          // Column for percentage
          headerName: '%',
          field: feature + '_percentage',
          type: 'string',
          renderHeader: () => (
            <Typography
              sx={{
                color: BlueberryColorsMap[feature],
                font: 'inherit',
              }}
            >
              %
            </Typography>
          ),
          valueGetter: (value, row) =>
            (row[feature as keyof BlockMeasureResult] as BlueberryTableCount)
              .percentage,
          renderCell: ({ row }: { row: BlockMeasureResult }) => {
            const entry = row[
              feature as keyof BlockMeasureResult
            ] as unknown as BlueberryTableCount;
            return (
              <Box sx={{ color: BlueberryColorsMap[feature] }}>
                {entry?.percentage != null
                  ? roundFractional(entry.percentage, 0)
                  : 'N/D'}
                %
              </Box>
            );
          },
        } as GridColDef,
      ]),
      {
        headerName: t('Distribution'),
        field: 'colorDistribution',
        // type: 'string',
        renderCell: (value) =>
          renderMiniBarChartCell(value, blueberriesInRipenessOrder),
      },
      {
        headerName: t('Total', { ns: 'ranch-view' }),
        field: 'totalEstimatedBerryCount',
        type: 'number',
        filterOperators: getGridNumericOperators(),
        valueFormatter: (value) =>
          formatCellContent(value, { maxFractionDigits: 0 }),
      },
      ...blueberriesInRipenessOrder.map(
        (feature) =>
          ({
            // Column for count value
            headerName: t(feature),
            minWidth: minColumnWidthSmall,
            field: feature + '_value_per_plant',
            headerClassName:
              feature === FeatureEnum.blueberry_Green ? 'left-divider' : null,
            type: 'string',
            renderHeader: () => (
              <Typography
                sx={{
                  color: BlueberryColorsMap[feature],
                  font: 'inherit',
                }}
              >
                {t(feature)}
              </Typography>
            ),
            valueFormatter: (value) =>
              formatCellContent(value, { maxFractionDigits: 0 }),
            valueGetter: (value, row) =>
              (row[feature as keyof BlockMeasureResult] as BlueberryTableCount)
                .perPlant,
          } as GridColDef)
      ),
      {
        headerName: t('Total', { ns: 'ranch-view' }),
        minWidth: minColumnWidthSmall,
        field: 'totalBerriesPerPlant',
        type: 'number',
        filterOperators: getGridNumericOperators(),
        valueFormatter: (value) =>
          formatCellContent(value, { maxFractionDigits: 0 }),
      },
      {
        headerName: t('blockDetail'),
        minWidth: minColumnWidthPrimary + 12,
        field: 'details',
        headerClassName: 'left-divider',
        disableExport: true,
        renderCell: ({ value, hasFocus }) => (
          <Button
            onClick={value}
            variant='contained'
            size='small'
            tabIndex={hasFocus ? 0 : -1}
            color='secondary'
          >
            <Box
              style={{
                overflow: 'hidden',
                whiteSpace: 'nowrap',
                textOverflow: 'ellipsis',
                maxWidth: '100%',
              }}
            >
              {t('blockDetail')}
            </Box>
          </Button>
        ),
      },
      {
        headerName: t('Download'),
        minWidth: minColumnWidthPrimary + 10,
        field: 'download',
        disableExport: true,
        renderCell: ({ value, hasFocus }) => (
          <Button
            onClick={value}
            variant='contained'
            size='small'
            tabIndex={hasFocus ? 0 : -1}
            color='secondary'
          >
            <Box
              style={{
                overflow: 'hidden',
                whiteSpace: 'nowrap',
                textOverflow: 'ellipsis',
                maxWidth: '100%',
              }}
            >
              {t('Download')}
            </Box>
          </Button>
        ),
      },
    ];
    return columns.map(
      (column) => Object.assign(column, commonColDefProps) as GridColDef
    );
  }, [t]);

  const columnGroupingModel: GridColumnGroupingModel = useMemo(
    () => [
      {
        groupId: 'estimatedFlowersPerPlant',
        description: 'Estimated Flowers Per Plant',
        headerAlign: 'center',
        headerClassName: 'estimatedFlowersPerPlant',
        children: [
          ...BlueberryFlowerStageFeatures.map((feature) => ({
            field: feature + '_value_per_plant',
          })),
          { field: 'totalFlowersPerPlant' },
        ],
        renderHeaderGroup: () => (
          <Typography
            sx={{ fontSize: '0.9rem', fontWeight: 'bold' }}
            whiteSpace='normal'
            textAlign='center'
          >
            {t('Estimated Flowers Per Plant', { ns: 'ranch-view' })}
          </Typography>
        ),
      },
      {
        groupId: 'totalFlowersScanned',
        description: 'Total Flowers Scanned',
        headerAlign: 'center',
        headerClassName: 'totalFlowersScanned',
        children: [
          { field: 'flowerStageDistribution' },
          { field: 'totalEstimatedFlowerCount' },
          ...BlueberryFlowerStageFeatures.map((feature) => ({
            field: feature + '_value',
          })),
        ],
        renderHeaderGroup: () => (
          <Typography
            sx={{ fontSize: '0.9rem', fontWeight: 'bold' }}
            whiteSpace='normal'
            textAlign='center'
          >
            {t('Total Flowers Scanned', { ns: 'ranch-view' })}
          </Typography>
        ),
      },
      {
        groupId: 'totalBerriesScanned',
        description: 'Total Berries Scanned',
        headerAlign: 'center',
        headerClassName: 'totalBerriesScanned',
        children: [
          { field: 'colorDistribution' },
          { field: 'totalEstimatedBerryCount' },
          ...blueberriesInRipenessOrder.flatMap((feature) => [
            { field: feature + '_value' },
            { field: feature + '_percentage' },
          ]),
        ],
        renderHeaderGroup: () => (
          <Typography sx={{ fontSize: '0.9rem', fontWeight: 'bold' }}>
            {t('Total Berries Scanned', { ns: 'ranch-view' })}
          </Typography>
        ),
      },
      {
        groupId: 'estimatedBerriesPerPlant',
        description: 'Estimated Berries Per Plant',
        headerAlign: 'center',
        headerClassName: 'estimatedBerriesPerPlant',
        children: [
          ...blueberriesInRipenessOrder.map((feature) => ({
            field: feature + '_value_per_plant',
          })),
          { field: 'totalBerriesPerPlant' },
        ],
        renderHeaderGroup: () => (
          <Typography sx={{ fontSize: '0.9rem', fontWeight: 'bold' }}>
            {t('Estimated Visible Berries Per Plant', { ns: 'ranch-view' })}
          </Typography>
        ),
      },
    ],
    [t]
  );

  return (
    <motion.div
      style={{
        width: '100%',
        height: '100%',
        minWidth: '50px',
        overflowX: 'auto',
        overflowY: 'hidden',
      }}
    >
      <Box
        ref={sizeRef}
        sx={{ width: '100%', height: '100%', position: 'relative' }}
      >
        <Box
          sx={{
            position: 'absolute',
            zIndex: 2,
            top: '0.8rem',
            right: '1.5rem',
          }}
        >
          <CanopyProvider>
            <RanchSummaryTableDropdowns
              showCountTypeDropdown={false}
              showDateRangeDropdown={true}
            />
          </CanopyProvider>
        </Box>
        <DataGrid
          loading={isLoadingCustomerSummary}
          apiRef={apiRef}
          columns={cells}
          rows={rows}
          disableRowSelectionOnClick
          disableVirtualization
          // ^^^ when enabled - throws problems with width
          // on loading or filtering (will expand to the right repeatedly)
          slots={{ toolbar: GridToolbar }}
          columnGroupingModel={columnGroupingModel}
          sx={{
            width: `${size.width}px`,
            height: `${size.height}px`,
            padding: '1rem',
            bgcolor: theme.colors.canopy.grey[25],
            border: 0,
            borderRadius: theme.borderRadius.primary,
            color: theme.colors.common.white,
            ['& .MuiDataGrid-main']: {
              mt: '1.5rem',
            },
            [`& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within`]: {
              outline: 'none',
            },
            [`& .MuiDataGrid-columnHeader:focus, & .MuiDataGrid-columnHeader:focus-within`]:
              {
                outline: 'none',
              },
            ['& svg']: {
              fill: theme.colors.common.white,
            },
            ['& .MuiTablePagination-root']: {
              color: theme.colors.common.white,
            },
            ['& .MuiDataGrid-toolbarContainer > button']: {
              color: theme.colors.common.white,
            },
            ['& .MuiDataGrid-virtualScrollerContent']: {
              maxWidth: '100%',
              width: '100%',
            },
            ['& .MuiDataGrid-row.Mui-selected']: {
              backgroundColor: theme.colors.common.darkGrey,
            },
            '& .MuiDataGrid-columnHeader': {
              borderBottom: `1px solid ${theme.colors.darkTheme.grey.muted}`,
              backgroundColor: theme.colors.canopy.grey[25],
            },
            '& .MuiDataGrid-row .MuiDataGrid-cell': {
              borderBottom: `1px solid ${theme.colors.darkTheme.grey.muted}`,
            },
            '& .MuiDataGrid-columnHeaders': {
              borderBottom: `1px solid ${theme.colors.darkTheme.grey.muted}`,
            },
            '& .MuiDataGrid-columnSeparator': {
              display: 'none',
            },
            '& .left-divider': {
              position: 'relative',
              '&::after': {
                content: '""',
                position: 'absolute',
                top: '15%',
                bottom: '15%',
                width: '1px',
                left: 0,
                backgroundColor: theme.colors.darkTheme.grey.muted,
              },
            },
            '& .totalBerriesScanned, & .totalFlowersScanned': {
              // backgroundColor: `${hexToRgb(theme.colors.darkTheme.blue).replace(
              //   ')',
              //   ', 0.4)'
              // )}`,
              backgroundColor: `${hexToRgb('#008B8B').replace(')', ', 0.8)')}`,
              // backgroundColor: `${hexToRgb('#348aa7').replace(')', ', 0.5)')}`,
              borderTopLeftRadius: theme.borderRadius.primary,
              borderTopRightRadius: theme.borderRadius.primary,
              p: '0.5rem',
              '& .MuiDataGrid-withBorderColor': {
                borderColor: 'transparent',
              },
            },
            '& .estimatedBerriesPerPlant, & .estimatedFlowersPerPlant': {
              // backgroundColor: `${hexToRgb('#FF6347').replace(')', ', 0.5)')}`,
              backgroundColor: `${hexToRgb(theme.colors.darkTheme.red).replace(
                ')',
                ', 0.5)'
              )}`,
              borderTopLeftRadius: theme.borderRadius.primary,
              borderTopRightRadius: theme.borderRadius.primary,
              p: '0.5rem',
              '& .MuiDataGrid-withBorderColor': {
                borderColor: 'transparent',
              },
            },
          }}
          slotProps={{
            pagination: {
              labelRowsPerPage: t('Rows per page'),
            },
            loadingOverlay: {
              variant: 'skeleton',
              noRowsVariant: 'skeleton',
            },
          }}
          localeText={{
            toolbarDensity: t('density'),
            toolbarDensityLabel: t('density'),

            toolbarColumns: t('columns'),
            toolbarColumnsLabel: t('columns'),

            toolbarFilters: t('filters'),
            toolbarFiltersLabel: t('filters'),

            toolbarExport: t('export'),
            toolbarExportLabel: t('export'),
          }}
        ></DataGrid>
      </Box>
    </motion.div>
  );
};

export default RanchSummaryTableBlueberry;

interface MiniBarChartProps {
  data: {
    percentage: number;
    feature: FeatureEnum;
  }[];
}

const renderMiniBarChartCell = (
  params: GridRenderCellParams<BlockMeasureResult>,
  features: FeatureEnum[]
) => {
  const percentages = features.map((feature) => {
    const entry = params.row[
      feature as keyof BlockMeasureResult
    ] as BlueberryTableCount;
    return entry && entry?.percentage != null
      ? { percentage: entry.percentage, feature }
      : { percentage: 0, feature };
  });
  return <MiniBarChart data={percentages} />;
};

const MiniBarChart: React.FC<MiniBarChartProps> = ({ data }) => {
  return (
    <div
      style={{
        width: '100%',
        height: '100%',
        display: 'flex',
        alignItems: 'center',
      }}
    >
      {data.map(({ percentage, feature }, index) => (
        <div
          key={index}
          style={{
            width: `${percentage}%`,
            height: '20px',
            backgroundColor: BlueberryColorsMap[feature],
          }}
        />
      ))}
    </div>
  );
};
